import { useEffect } from "react";
import { useSetPosOrderDeliveryCost } from "../atoms/usePosOrderDeliveryCostAtom";
import { useSetPosOrderDeliveryMethod } from "../atoms/usePosOrderDeliveryMethodAtom";
import { useSetPosOrderCustomDiscount } from "../atoms/usePosOrderCustomDiscount";
import { useSetPosOrderPayment } from "../atoms/usePosOrderPaymentAtom";
import { useSetPosOrderRemark } from "../atoms/usePosOrderRemarkAtom";
import { useSetPosOrderUser } from "../atoms/usePosOrderUser";
import useCartValidationPost from "../mutations/cart/useCartValidationPost";
import { CartContentType, DeliveryMethods } from "../utils/enum";
import {
  getCustomDiscountFromCartContent,
  objectIsEmpty,
} from "../utils/helpers";
import useDiscountCodeHook from "./useDiscountCodeHook";
import useIsOrderEditingHook from "./useIsOrderEditingHook";
import { useOneTimeEffect } from "./useOneTimeEffect";
import usePosOrderStandardHook from "./usePosOrderStandardHook";
import usePosSettingsHook from "./usePosSettingsHook";
import usePosOrderTipHook from "./usePosOrderTipHook";
import { useSetPosTakeout } from "../atoms/usePosTakeout";
import useConvertCartContentToCartHook from "./useConvertCartContentToCartHook";
import { useSetCart } from "./useCartHook";
import useCategoryQueryHook from "./useCategoryQueryHook";
import { useSetPosOrderServiceCost } from "../atoms/usePosOrderServiceCostAtom";

const usePosOrderSetupHook = () => {
  const { settings, isPending } = usePosSettingsHook();
  const { isOrderEditing, editOrder } = useIsOrderEditingHook();
  const setCart = useSetCart();
  const setDeliveryMethod = useSetPosOrderDeliveryMethod();
  const setPosOrderUser = useSetPosOrderUser();
  const setPosOrderPayment = useSetPosOrderPayment();
  const setPosOrderDeliveryCost = useSetPosOrderDeliveryCost();
  const setPosOrderRemark = useSetPosOrderRemark();
  const { setTip: setPosOrderTip } = usePosOrderTipHook();
  const { setStandardDeliveryMethod, setStandardPaymentMethod } =
    usePosOrderStandardHook();
  const mutation = useCartValidationPost();
  const { setDiscountCode } = useDiscountCodeHook();
  const setCustomDiscount = useSetPosOrderCustomDiscount();
  const setPosTakeout = useSetPosTakeout();
  const convertCartContentToCart = useConvertCartContentToCartHook();
  const setPosOrderServiceCost = useSetPosOrderServiceCost();
  const { products } = useCategoryQueryHook();

  useOneTimeEffect({
    condition: isPending === false && objectIsEmpty(settings) === false,
    effect: () => {
      setStandardDeliveryMethod();
      setStandardPaymentMethod();
    },
  });

  const isEditing = editOrder?.isEditing ?? true;

  useEffect(() => {
    if (isOrderEditing && isEditing) {
      const newCart = convertCartContentToCart(editOrder.cart_content);
      const customDiscountData = getCustomDiscountFromCartContent(
        editOrder.cart_content
      );
      customDiscountData && setCustomDiscount(customDiscountData);
      const isTakeout =
        editOrder.order_information.delivery_method_id ===
        DeliveryMethods.TAKEOUT;
      setPosTakeout(isTakeout);
      const deliveryMethodId = isTakeout
        ? DeliveryMethods.POS_ORDER
        : editOrder.order_information.delivery_method_id;
      setDeliveryMethod(deliveryMethodId);
      setPosOrderUser(editOrder.order_user);
      setPosOrderPayment(editOrder.order_information.payment_method_id);
      setPosOrderDeliveryCost(
        editOrder.cart_content.find(
          (c) => c.type === CartContentType.DELIVERY_COST
        )?.single_price_inc_vat ?? ""
      );
      setPosOrderRemark(editOrder.order_information.remarks ?? "");
      setPosOrderTip(
        parseInt(editOrder.order_information.tip ?? "0") !== 0
          ? editOrder.order_information.tip
          : "-1"
      );
      setPosOrderServiceCost(
        editOrder.cart_content.find(
          (c) => c.type === CartContentType.SERVICE_COST
        )?.single_price_inc_vat ?? ""
      );

      if (editOrder.order_information.discount_code_value) {
        setDiscountCode({
          code: editOrder.order_information.discount_code_value,
          isApplied: false,
        });
        mutation.mutateAsync({
          deliveryMethod: deliveryMethodId,
          zipCode: editOrder.order_user.zip_code,
          discountCode: editOrder.order_information.discount_code_value,
          cart: newCart,
        });
      }
    }
  }, [editOrder?.order_information?.item_id, isOrderEditing, isEditing]);

  //TODO: rethink this
  useEffect(() => {
    if (isOrderEditing && products && isEditing) {
      const newCartContent = editOrder.cart_content
        .filter(
          (c) =>
            c.type === CartContentType.PRODUCT ||
            c.type === CartContentType.CUSTOM
        )
        .map((item) => {
          const product = products.find(
            (product) => product.item_id === parseInt(item.menu_item_id)
          );
          return {
            ...item,
            sub_content: product?.sub_content ?? [],
          };
        });
      const newCart = convertCartContentToCart(newCartContent);
      setCart(newCart);
    }
  }, [
    products,
    isOrderEditing,
    editOrder?.order_information?.item_id,
    isEditing,
  ]);
};

export default usePosOrderSetupHook;
